/**
 * Apply Enforcement Modes — FG4 Fase A: Shadow / Dry-run wiring
 *
 * ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §FG4.
 *
 * THE PROBLEM
 * ===========
 * Coach-level-by-default pedagogy + permissive `return OK` schema defaults are
 * a cliff-edge: flipping strictness without a ramp drops 30%+ of high-tier
 * production questions into Severity-70 blockers in a single commit. The risk
 * is operational, not didactic — good questions rejected on MISSING METADATA.
 *
 * THE CONTRACT
 * ============
 * Between dedup and gate-evaluation, we partition the issue set by the
 * per-module / per-type enforcement mode resolved from GovernanceConfigOverrides.
 *
 *   off      → drop (legacy permissive behaviour; was the implicit default).
 *   shadow   → record as ShadowFinding, REMOVE from gating issues (no block).
 *   warn     → downgrade blocker → coach (surfaces in UI, never blocks).
 *   enforce  → keep as-is (full strictness).
 *
 * Grace ledger entries can additionally degrade severity within enforce mode
 * for a NAMED, OWNED, EXPIRING exception (anti-bandaid principle #3).
 *
 * Design principles (mirroring §F0.A):
 *   - PURE: no module-level state; same inputs → same outputs.
 *   - DEPENDENCY-INVERTED: the orchestrator injects the config + shadow sink.
 *     This function never touches dataLayer, react, or any singleton.
 *   - BACKWARDS-COMPATIBLE: when `config` is undefined the function is a no-op
 *     (returns the input issues unchanged). Roll-out is opt-in.
 */

import type { ValidationIssue, IssueDomain } from './issueContract'
import {
  type GovernanceConfigOverrides,
  type EnforcementMode,
  resolvePedagogicalMode,
  resolveSchemaMode,
  findActiveGrace,
} from './governanceConfigStore'
import type { ShadowFinding, ShadowSource } from '../shadow/shadowFindings'

/**
 * Sink interface for shadow findings. The orchestrator passes a thin adapter
 * around `recordShadowFinding` so this module stays free of side-effect
 * imports (testability + DDD boundary).
 */
export interface ShadowSink {
  record(finding: Omit<ShadowFinding, 'detectedAt'>): void
}

export interface EnforcementInputs {
  /** Issues remaining after dedup, before gate evaluation. */
  issues: ValidationIssue[]
  /** Resolved governance config (per-module/type modes + grace ledger). */
  config: GovernanceConfigOverrides | undefined
  /** Shadow sink — orchestrator injects an adapter around the module store. */
  shadowSink: ShadowSink
  /** Module code derived once by the caller (e.g. 'MET' from 'MET-COORDINATEN'). */
  module: string
  /** Question id (for shadow findings + grace ledger). */
  questionId: string
  /** Question family id (for shadow findings + grace ledger). */
  family: string
  /** Question tier (for shadow findings + tier-band ramp telemetry). */
  tier: number
  /** Question type (for resolveSchemaMode). May be undefined. */
  questionType: string | undefined
}

/**
 * Cross-module INFORMATIONAL migration counters that must ALWAYS surface in the
 * live report, regardless of a module's FG4 ramp mode.
 *
 * THE BUG THIS FIXES
 * ==================
 * `PEDAGOGY_MISCONCEPTION_EN_LEGACY` is a pure `info` migration counter — its
 * entire job is to give cross-module visibility into EN→NL ID drift while the
 * §6 cleanup proceeds. But because it is a `pedagogy`-domain issue, the ramp
 * routes it by `resolvePedagogicalMode(module)`. With REK on `'warn'` and the
 * other 9 modules on `'shadow'`, the counter was diverted to the shadow store
 * for every module EXCEPT REK — so the Code Registry showed it "stuck on REK"
 * even though every module has live EN-IDs. That defeats the counter's purpose.
 *
 * THE FIX (structural, not a bandaid)
 * ===================================
 * These codes are `info` by construction (severity COSMETIC, never blockers),
 * so surfacing them live can NEVER push a question off the FG4 cliff — the
 * ramp exists to gate *blockers*, not telemetry. We treat them as 'enforce'-
 * routed (= retained as-is) so they always reach `batchResult`. The ramp keeps
 * full control over real pedagogy blockers; only these named, never-blocking
 * migration counters are exempt. Anti-bandaid principle #3: the exemption is a
 * NAMED, documented allowlist, not a silent global override.
 */
const ALWAYS_SURFACE_INFO_CODES: ReadonlySet<string> = new Set<string>([
  'PEDAGOGY_MISCONCEPTION_EN_LEGACY',
])

/**
 * Map an IssueDomain to the EnforcementMode "scope" used in the config:
 *   - always-surface info counters → 'enforce' (retained live, never gated away)
 *   - pedagogy issues   → resolved by resolvePedagogicalMode(module)
 *   - schema/type       → resolved by resolveSchemaMode(questionType)
 *   - everything else   → 'enforce' (current behaviour, no ramp configured)
 *
 * Anti-bandaid principle: only the explicitly-staged domains opt into the
 * ramp; we never silently downgrade an unrelated check.
 */
function resolveModeForIssue(
  issue: ValidationIssue,
  input: EnforcementInputs,
): EnforcementMode {
  const cfg = input.config
  if (!cfg) return 'enforce'

  // Cross-module info migration counters bypass the ramp so they always reach
  // the live report (see ALWAYS_SURFACE_INFO_CODES). They are info-only, so
  // 'enforce' here means "retain as-is" — it never gates a question.
  if (ALWAYS_SURFACE_INFO_CODES.has(issue.code)) return 'enforce'

  switch (issue.domain as IssueDomain) {
    case 'pedagogy':
      return resolvePedagogicalMode(cfg, input.module)
    case 'schema':
    case 'type':
      return input.questionType
        ? resolveSchemaMode(cfg, input.questionType)
        : 'enforce'
    default:
      return 'enforce'
  }
}

function shadowSourceFor(domain: IssueDomain): ShadowSource {
  switch (domain) {
    case 'pedagogy':
      return 'pedagogy'
    case 'schema':
      return 'schema'
    case 'type':
      return 'type'
    case 'content':
      return 'content'
    default:
      // Defensive — caller only ramps the four domains above. Anything else
      // hitting this branch is mis-configured; treat as content for telemetry.
      return 'content'
  }
}

export interface EnforcementResult {
  /** Issues that should continue to gate evaluation. */
  retainedIssues: ValidationIssue[]
  /** Per-mode tally for telemetry / admin debugging. */
  modeCounts: Record<EnforcementMode, number>
  /** Number of findings forwarded to the shadow sink. */
  shadowed: number
  /** Number of blockers downgraded to coaches via warn mode. */
  downgraded: number
  /** Number of issues whose severity was degraded by an active grace ledger entry. */
  graceDegraded: number
}

/**
 * Apply the per-issue enforcement mode + grace ledger. Pure — does NOT mutate
 * the input issue array. Returns the retained issues plus telemetry counters.
 */
export function applyEnforcementModes(
  input: EnforcementInputs,
): EnforcementResult {
  const modeCounts: Record<EnforcementMode, number> = {
    off: 0,
    shadow: 0,
    warn: 0,
    enforce: 0,
  }
  let shadowed = 0
  let downgraded = 0
  let graceDegraded = 0

  const retained: ValidationIssue[] = []

  for (const issue of input.issues) {
    const mode = resolveModeForIssue(issue, input)
    modeCounts[mode] += 1

    if (mode === 'off') {
      // Legacy permissive — silently dropped. No telemetry; mirrors the
      // pre-FG4 coach-level-by-default behaviour for pedagogy.
      continue
    }

    if (mode === 'shadow') {
      input.shadowSink.record({
        questionId: input.questionId,
        family: input.family,
        module: input.module,
        tier: input.tier,
        source: shadowSourceFor(issue.domain as IssueDomain),
        wouldBlockReason: issue.code,
        severity: issue.severity,
      })
      shadowed += 1
      continue
    }

    if (mode === 'warn') {
      if (issue.kind === 'blocker') {
        retained.push({ ...issue, kind: 'coach' })
        downgraded += 1
      } else {
        retained.push(issue)
      }
      continue
    }

    // mode === 'enforce' — check grace ledger for a named exception.
    const grace = input.config
      ? findActiveGrace(input.config, {
          module: input.module,
          family: input.family,
          questionId: input.questionId,
        })
      : undefined

    if (grace && issue.kind === 'blocker') {
      const degraded = grace.degradeToSeverity ?? 50
      retained.push({
        ...issue,
        severity: Math.min(issue.severity, degraded),
        kind: degraded >= 70 ? 'blocker' : 'coach',
      })
      graceDegraded += 1
      continue
    }

    retained.push(issue)
  }

  return {
    retainedIssues: retained,
    modeCounts,
    shadowed,
    downgraded,
    graceDegraded,
  }
}

/**
 * Convenience: derive the module code from a family id like 'MET-COORDINATEN'.
 * Returns an empty string when the family id is malformed — the caller
 * should treat that as "no module" and fall through to default behaviour.
 */
export function deriveModuleFromFamily(familyId: string | undefined): string {
  if (!familyId) return ''
  const idx = familyId.indexOf('-')
  if (idx <= 0) return familyId
  // TGL-* seeds live under either GELD or TIJD modules; the rulebooks treat
  // them as their own pseudo-modules. Keep the prefix as-is — the config
  // store keys on whatever the caller uses (rulebook id is the SSOT).
  return familyId.slice(0, idx)
}
